import { buildCloudFrontUrl } from '@/utils/pathUtils';
import store from '@/store';
import type { UserFont } from '@/composables/fonts';
import { getFonts } from '@/api/fonts';
require('@/angular-app/libs/BoundedText/Font.js');

export const DEFAULT_FONT = 'arial.ttf';

class FontManagerService {
    private static instance: FontManagerService;
    private _currentFonts: UserFont[] = [];
    private _currentFontsMap: Map<string, UserFont> = new Map();
    private _fontsInDOM = new Set<string>();
    private defaultFontPath = '';
    private fontLoadingPromise: Promise<UserFont[]> | null = null;
    private fontsRefreshPromise: Promise<UserFont[]> | null = null;
    // Paths still missing after a refresh, so templates full of a deleted font don't refetch per element
    private _unresolvedPaths = new Set<string>();
    private readonly _maxFontsToDownload = 15;
    private readonly Font = window.Font;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private constructor() {}

    public static getInstance(): FontManagerService {
        if (!FontManagerService.instance) {
            FontManagerService.instance = new FontManagerService();
        }

        return FontManagerService.instance;
    }

    public get defaultFont(): UserFont {
        return this._currentFontsMap.get(this.defaultFontPath)!;
    }

    public getDefaultFont(): UserFont {
        return this.defaultFont;
    }

    public get maxFontsToDownload(): number {
        return this._maxFontsToDownload;
    }

    public get loadedFonts() {
        return this._currentFonts;
    }

    public clearFontsCache() {
        this._currentFonts = [];
        this.fontLoadingPromise = null;
        this._currentFontsMap = new Map();
        this._unresolvedPaths.clear();
        this.Font.clearCache();
    }

    public async getAllFonts(forceReload = false): Promise<UserFont[]> {
        if (!forceReload && this.fontLoadingPromise && this._currentFonts.length) {
            return this._currentFonts;
        }

        const fonts = await getFonts();
        // Path is the unique identity of a font. Duplicate rows produce options that are
        // indistinguishable to vue-select, which keys the dropdown on the option contents:
        // two identical keys crash Vue's updateChildren and wedge the scheduler for good.
        this._currentFontsMap = new Map(fonts.map((font) => [font.Path, font]));
        this._currentFonts = [...this._currentFontsMap.values()].sort((a: UserFont, b: UserFont) =>
            a.Name.localeCompare(b.Name)
        );
        store.commit('userFonts', this._currentFonts);
        return this._currentFonts;
    }

    public async initFonts(forceReload = false): Promise<UserFont[]> {
        if (forceReload || !this.fontLoadingPromise) {
            console.log('Initializing Fonts');

            // eslint-disable-next-line no-async-promise-executor
            this.fontLoadingPromise = new Promise(async (resolve, reject) => {
                try {
                    await this.getAllFonts(forceReload);

                    this.loadFontChunksToDOM(this._currentFonts.slice(0, this.maxFontsToDownload));

                    const index = this._currentFonts.findIndex((font) => font.Name.toLowerCase() === 'arial');
                    if (index === -1) {
                        throw new Error('Cannot find Arial font');
                    }

                    this.defaultFontPath = this._currentFonts[index].Path;
                    this.loadFontToDOM(this._currentFonts[index]);
                    try {
                        const name = this._currentFonts[index].Name + '_' + this._currentFonts[index].Style;
                        await this.Font.loadAsync(name, buildCloudFrontUrl(this.defaultFontPath));
                    } catch (error) {
                        const newError = new Error(
                            `Failed to loadAsync: ${(error as Error).message} - ${buildCloudFrontUrl(
                                this.defaultFontPath
                            )} - ${this._currentFonts[index].Name + '_' + this._currentFonts[index].Style}`
                        );
                        window.rumReportError(newError);
                    }

                    console.log('Fonts Initialized :)');
                    resolve(this._currentFonts);
                } catch (error) {
                    const newError = new Error(`Failed to initialize fonts: ${(error as Error).message}`);
                    window.rumReportError(newError);
                    window.rumReportError(error as Error);
                    reject(error);
                }
            });
        }
        return this.fontLoadingPromise;
    }

    /**
     * Useful to load font family into canvas elements
     */
    public async loadFontFromPath(fontPath: string): Promise<string> {
        const font = await this.findFontByPath(fontPath);
        const fontName = `${font.Name}_${font.Style}`;

        // If font is already cached just return it
        if (this.Font.hasFont(fontName)) {
            return fontName;
        }

        // Otherwise load the font file
        const path = buildCloudFrontUrl(font.Path);
        return this.Font.loadAsync(fontName, path).then(() => fontName);
    }

    public getFontFromPath(path: string): UserFont | undefined {
        if (!path || typeof path !== 'string') return undefined;

        const exactMatch = this._currentFontsMap.get(path);
        if (exactMatch) return exactMatch;

        // Same font file can be referenced under a different folder (e.g. another account's fonts path)
        const fileName = this.getFileName(path);
        if (!fileName) return undefined;
        return this._currentFonts.find((font) => this.getFileName(font.Path) === fileName);
    }

    private getFileName(path: string): string {
        return typeof path === 'string' ? path.split('/').pop() ?? '' : '';
    }

    public async loadFontChunksToDOM(fonts: UserFont[], size = this.maxFontsToDownload) {
        for (let i = 0; i < fonts.length; i += size) {
            const chunk = fonts.slice(i, i + size + 1).map((font) => this.loadFontToDOM(font));
            await Promise.all(chunk);
        }
    }

    public async loadFontToDOM(font: UserFont) {
        const fontName = `${font.Name} (${font.Style})`;
        const safeFontName = this.generateSafeFontName(fontName);
        if (this.doesFontExist(safeFontName) || this._fontsInDOM.has(safeFontName)) return;

        try {
            this._fontsInDOM.add(safeFontName);
            await this.addFontFaceToDocument(safeFontName, `url("${buildCloudFrontUrl(font.Path)}")`);
        } catch (error) {
            console.error(`Error loading font: ${safeFontName}`, error);
        }
    }

    private async addFontFaceToDocument(fontFamily: string, source: string) {
        const fontFace = new FontFace(fontFamily, source);
        await fontFace.load();
        document.fonts.add(fontFace);
    }

    private doesFontExist(fontName: string): boolean {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const text = 'abcdefghijklmnopqrstuvwxyz0123456789';
        context!.font = '72px monospace';
        const baselineSize = context!.measureText(text).width;
        context!.font = `72px '${fontName}', monospace`;
        const newSize = context!.measureText(text).width;
        return newSize !== baselineSize;
    }

    /**
     * Resolves a font path, refetching the user fonts once if it's not in the cached list. Falls back to the default font
     */
    public async findFontByPath(fontPathToMatch: string): Promise<UserFont> {
        let font = this.getFontFromPath(fontPathToMatch);

        if (!font && fontPathToMatch && !this._unresolvedPaths.has(fontPathToMatch)) {
            try {
                await this.refreshFonts();
                font = this.getFontFromPath(fontPathToMatch);
                if (!font) this._unresolvedPaths.add(fontPathToMatch);
            } catch (error) {
                console.error('findFontByPath: Failed to refresh fonts', error);
            }
        }

        if (font) return font;

        const defaultFont = this.getDefaultFont();
        console.warn(
            `findFontByPath: Couldn't find font with path "${fontPathToMatch}". Falling back to default font ${defaultFont.Name} (${defaultFont.Path}).`
        );
        return defaultFont;
    }

    private refreshFonts(): Promise<UserFont[]> {
        // Texts load in parallel chunks: concurrent misses share a single request
        this.fontsRefreshPromise ??= this.getAllFonts(true).finally(() => {
            this.fontsRefreshPromise = null;
        });
        return this.fontsRefreshPromise;
    }

    public generateSafeFontName(name: string): string {
        return (name || '').replace(/[^\w-]/g, '_');
    }
}

export default FontManagerService.getInstance();
